// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Params = Record<string, any>

export type HttpResponseType = 'json' | 'text' | 'blob' | 'arraybuffer'

export type HttpRequestConfig = {
  params?: Params
  paramsSerializer?: (params: Params) => string
  headers?: Record<string, string | undefined>
  timeout?: number
  signal?: AbortSignal
  responseType?: HttpResponseType
  data?: unknown
}

export type HttpResponse<T = unknown> = {
  data: T
  status: number
  statusText: string
  headers: Headers
}

export type HttpErrorCode =
  | 'ERR_BAD_REQUEST'
  | 'ERR_BAD_RESPONSE'
  | 'ERR_NETWORK'
  | 'ERR_CANCELED'
  | 'ECONNABORTED'

export class HttpError<T = unknown> extends Error {
  override name = 'HttpError'
  readonly status?: number

  constructor(
    message: string,
    readonly code: HttpErrorCode,
    readonly response?: HttpResponse<T>
  ) {
    super(message)
    this.status = response?.status
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isHttpError = <T = any>(error: unknown): error is HttpError<T> => error instanceof HttpError

export type HttpClientOptions = {
  baseURL?: string
  headers?: Record<string, string>
  timeout?: number
  paramsSerializer?: (params: Params) => string
  prepareHeaders?: (headers: Headers) => void | Promise<void>
  onError?: (error: HttpError) => void
}

const isAbsoluteURL = (url: string) => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)

const sharesBaseOrigin = (url: string, baseURL: string | undefined) => {
  if (!isAbsoluteURL(url)) return true
  if (!baseURL) return false
  try {
    return new URL(url, baseURL).origin === new URL(baseURL).origin
  } catch {
    return false
  }
}

const joinURL = (baseURL: string | undefined, url: string) =>
  baseURL && !isAbsoluteURL(url) ? `${baseURL.replace(/\/?\/$/, '')}/${url.replace(/^\/+/, '')}` : url

const serializeFlatParams = (params: Params) => {
  const search = new URLSearchParams()
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value == null) return
    const values: unknown[] = Array.isArray(value) ? value : [value]
    values.forEach((v) => search.append(key, String(v)))
  })
  return search.toString()
}

const appendQuery = (url: string, query: string) => {
  if (!query) return url
  const withoutHash = url.split('#')[0]
  return `${withoutHash}${withoutHash.includes('?') ? '&' : '?'}${query}`
}

const isJSON = (text: string) => {
  try {
    JSON.parse(text)
    return true
  } catch {
    return false
  }
}

const encodeBody = (data: unknown, headers: Headers): BodyInit | null | undefined => {
  if (data === undefined) {
    headers.delete('Content-Type')
    return undefined
  }
  if (data === null) return null
  if (data instanceof FormData) {
    headers.delete('Content-Type')
    return data
  }
  if (
    data instanceof Blob ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  )
    return data as BodyInit
  if (typeof data === 'string') {
    const sendsJSON = headers.get('Content-Type')?.includes('application/json')
    return sendsJSON && !isJSON(data) ? JSON.stringify(data) : data
  }
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  return JSON.stringify(data)
}

const parseBody = async (response: Response, responseType: HttpResponseType) => {
  if (responseType === 'blob') return response.blob()
  if (responseType === 'arraybuffer') return response.arrayBuffer()
  const text = await response.text()
  if (responseType === 'text' || !text) return text
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export function createHttpClient(options: HttpClientOptions = {}) {
  const defaultHeaders = { Accept: 'application/json, text/plain, */*', ...options.headers }

  async function request<T>(
    method: string,
    url: string,
    body: unknown,
    config: HttpRequestConfig = {}
  ): Promise<HttpResponse<T>> {
    const headers = new Headers(defaultHeaders)
    Object.entries(config.headers ?? {}).forEach(
      ([key, value]) => value !== undefined && headers.set(key, value)
    )

    const serialize = config.paramsSerializer ?? options.paramsSerializer ?? serializeFlatParams
    const fullURL = appendQuery(joinURL(options.baseURL, url), config.params ? serialize(config.params) : '')

    const controller = new AbortController()
    const timeout = config.timeout ?? options.timeout ?? 0
    let timedOut = false
    const timer = timeout > 0 ? setTimeout(() => ((timedOut = true), controller.abort()), timeout) : undefined
    const abortFromCaller = () => controller.abort()
    config.signal?.addEventListener('abort', abortFromCaller)

    const fail = (error: HttpError): never => {
      options.onError?.(error)
      throw error
    }

    try {
      if (config.signal?.aborted) fail(new HttpError('canceled', 'ERR_CANCELED'))
      if (sharesBaseOrigin(url, options.baseURL)) await options.prepareHeaders?.(headers)
      const requestBody = encodeBody(body, headers)

      let response: HttpResponse<T>
      try {
        const res = await fetch(fullURL, { method, headers, body: requestBody, signal: controller.signal })
        response = {
          data: (await parseBody(res, config.responseType ?? 'json')) as T,
          status: res.status,
          statusText: res.statusText,
          headers: res.headers,
        }
      } catch (error) {
        if (timedOut) return fail(new HttpError(`timeout of ${timeout}ms exceeded`, 'ECONNABORTED'))
        if (controller.signal.aborted) return fail(new HttpError('canceled', 'ERR_CANCELED'))
        return fail(Object.assign(new HttpError('Network Error', 'ERR_NETWORK'), { cause: error }))
      }

      if (response.status < 200 || response.status >= 300) {
        fail(
          new HttpError(
            `Request failed with status code ${response.status}`,
            response.status < 500 ? 'ERR_BAD_REQUEST' : 'ERR_BAD_RESPONSE',
            response
          )
        )
      }
      return response
    } finally {
      clearTimeout(timer)
      config.signal?.removeEventListener('abort', abortFromCaller)
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  return {
    get: <T = any, R = HttpResponse<T>>(url: string, config?: HttpRequestConfig) =>
      request<T>('GET', url, undefined, config) as Promise<R>,
    delete: <T = any, R = HttpResponse<T>>(url: string, config?: HttpRequestConfig) =>
      request<T>('DELETE', url, config?.data, config) as Promise<R>,
    post: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: HttpRequestConfig) =>
      request<T>('POST', url, data, config) as Promise<R>,
    put: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: HttpRequestConfig) =>
      request<T>('PUT', url, data, config) as Promise<R>,
    patch: <T = any, R = HttpResponse<T>>(url: string, data?: unknown, config?: HttpRequestConfig) =>
      request<T>('PATCH', url, data, config) as Promise<R>,
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export type HttpClient = ReturnType<typeof createHttpClient>

export const httpClient = createHttpClient()
